// SF2 — Delivery Performance Tracker.
//
// EVERY NUMBER HERE IS A PROMISE, NOT AN OUTCOME. Amazon India returns an
// estimated arrival date and no confirmed delivery date, so a missed promise is
// invisible on this page. The copy says so rather than letting the seller read
// these as delivery times that were achieved.
//
// The geography is postal — zone, circle, sorting district — because delivery
// time is set by courier routing rather than state boundaries. It deliberately
// does not reconcile with the state figures on Geographic Sales Intelligence.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FiAlertTriangle, FiChevronDown, FiChevronRight, FiInfo } from 'react-icons/fi';

import AuthContext from '../../../../context/AuthContext';
import local from '../GeographicOrders.module.css';
import { getDelivery, getDeliveryProducts } from '../services/geographicOrdersApi';

const GRAINS = [
  { key: 'zone', label: 'Zone' },
  { key: 'circle', label: 'Circle' },
  { key: 'district', label: 'Sorting district' },
];

const inr = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

const Blank = ({ title }) => <span className={local.blank} title={title}>—</span>;

// Days are rendered to one decimal: the promise is a date, and pretending to
// two decimals of precision on a derived figure would overstate it.
const Days = ({ timing }) => {
  if (!timing || timing.median_days === null || timing.median_days === undefined) {
    return <Blank title={timing?.reason || 'No figure'} />;
  }
  return <>{timing.median_days.toFixed(1)}d</>;
};

const DeliveryTable = ({ window: reportingWindow, onLoadingChange }) => {
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;
  const { start, end } = reportingWindow || {};

  const [grain, setGrain] = useState('circle');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);
  const [products, setProducts] = useState({});

  const load = useCallback(async () => {
    if (!token || !start || !end) return;
    setLoading(true);
    onLoadingChange?.(true);
    setError(null);
    setOpen(null);
    try {
      setData(await getDelivery(token, { start, end, grain }));
    } catch (err) {
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  }, [token, start, end, grain, onLoadingChange]);

  useEffect(() => { load(); }, [load]);

  // Unmounting mid-load would otherwise leave the page's Apply button
  // stuck reporting this component as still running.
  useEffect(() => () => onLoadingChange?.(false), [onLoadingChange]);

  // The product view lives inside a region, never across the country: a
  // national ranking would surface products with unlucky geography rather than
  // products with a delivery problem.
  const expand = async (row) => {
    if (open === row.region) { setOpen(null); return; }
    setOpen(row.region);
    if (products[row.region]) return;
    try {
      const result = await getDeliveryProducts(token, {
        start, end, grain, region: row.region, fulfilment: 'Amazon',
      });
      setProducts((current) => ({ ...current, [row.region]: result }));
    } catch (err) {
      setProducts((current) => ({ ...current, [row.region]: { error: err.message } }));
    }
  };

  const rows = data?.rows || [];
  const types = data?.fulfilment_types || [];

  return (
    <section className={local.card}>
      <div className={local.cardHead}>
        <h2 className={local.cardTitle}>What delivery speed each region is promised</h2>
        <div className={local.segmented}>
          {GRAINS.map((option) => (
            <button key={option.key} type="button"
              className={option.key === grain ? local.segmentOn : local.segment}
              onClick={() => setGrain(option.key)}>
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className={`${local.banner} ${local.bannerWarn}`}>
        <FiInfo className={local.bannerIcon} />
        <div>
          <strong>These are promises, not outcomes</strong>
          <div>
            Amazon returns an estimated arrival date and never confirms delivery,
            so this shows what customers were told to expect. Whether the promise
            was kept is not something Amazon exposes by region.
          </div>
        </div>
      </div>

      {error && (
        <div className={`${local.banner} ${error.reportUnavailable ? local.bannerWarn : local.bannerError}`}>
          <FiAlertTriangle className={local.bannerIcon} />
          <div>
            <strong>
              {error.notConnected ? 'No Amazon account connected'
                : error.reportUnavailable ? 'Data not ready' : 'Could not load delivery figures'}
            </strong>
            <div>{error.message}</div>
          </div>
        </div>
      )}

      {!error && (
        <div className={local.tableScroll}>
          <table className={local.table}>
            <thead>
              <tr>
                <th />
                <th>Region</th>
                <th className={local.numeric}>Shipments</th>
                <th className={local.numeric}>Median</th>
                <th className={local.numeric}>Slow tail (p90)</th>
                {types.map((type) => (
                  <th key={type} className={local.numeric}>{type}</th>
                ))}
                <th>Served from</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td className={local.emptyCell} colSpan={6 + types.length}>
                    {loading ? 'Loading…' : 'No shipments in this period.'}
                  </td>
                </tr>
              ) : rows.map((row) => (
                <React.Fragment key={row.region}>
                  <tr className={local.drillable} onClick={() => expand(row)}>
                    <td className={local.rankCell}>
                      {open === row.region ? <FiChevronDown /> : <FiChevronRight />}
                    </td>
                    <td className={row.unassigned ? local.unassigned : undefined}>
                      {row.label}
                    </td>
                    <td className={local.numeric}>{inr.format(row.shipments)}</td>
                    <td className={local.numeric}><Days timing={row.overall} /></td>
                    <td className={local.numeric}>
                      {row.overall.p90_days === null || row.overall.p90_days === undefined
                        ? <Blank title={row.overall.reason} />
                        : `${row.overall.p90_days.toFixed(1)}d`}
                    </td>
                    {types.map((type) => (
                      <td key={type} className={local.numeric}>
                        {row.by_fulfilment[type]
                          ? <Days timing={row.by_fulfilment[type]} />
                          : <Blank title="No shipments of this type here" />}
                      </td>
                    ))}
                    <td className={local.centres}>
                      {row.centres.map((centre) => centre.centre).join(', ') || '—'}
                      {row.centre_count > row.centres.length
                        && ` +${row.centre_count - row.centres.length}`}
                    </td>
                  </tr>

                  {/* Geography held constant, so what is left is the product. */}
                  {open === row.region && (
                    <tr className={local.nested}>
                      <td />
                      <td colSpan={5 + types.length}>
                        {!products[row.region] ? 'Loading products…'
                          : products[row.region].error ? products[row.region].error
                            : products[row.region].rows.length === 0
                              ? 'No Amazon-fulfilled products with a promise here.'
                              : (
                                <table className={local.innerTable}>
                                  <thead>
                                    <tr>
                                      <th>Amazon-fulfilled product</th>
                                      <th className={local.numeric}>Shipments</th>
                                      <th className={local.numeric}>Median</th>
                                      <th className={local.numeric}>p90</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {products[row.region].rows.slice(0, 10).map((item) => (
                                      <tr key={item.product}>
                                        <td>{item.product}</td>
                                        <td className={local.numeric}>{inr.format(item.shipments)}</td>
                                        <td className={local.numeric}>
                                          {item.median_days === null
                                            ? <Blank title="Too few shipments" />
                                            : `${item.median_days.toFixed(1)}d`}
                                        </td>
                                        <td className={local.numeric}>
                                          {item.p90_days === null
                                            ? <Blank title="Too few shipments" />
                                            : `${item.p90_days.toFixed(1)}d`}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data?.coverage && (
        <div className={local.coverage}>
          {inr.format(data.coverage.shipments)} shipments
          {data.coverage.no_promise_shipments > 0 && (
            <>, {inr.format(data.coverage.no_promise_shipments)} merchant-fulfilled
            with no published promise</>
          )}
          {data.coverage.undated_shipments > 0 && (
            <>, {inr.format(data.coverage.undated_shipments)} with an unusable
            arrival date</>
          )}
          {data.coverage.unassigned_shipments > 0 && (
            <>, {inr.format(data.coverage.unassigned_shipments)} with no readable
            pincode</>
          )}. Regions below {data.min_shipments} shipments show volume but no timing.
        </div>
      )}

      {data?.notes?.length > 0 && (
        <ul className={local.noteList}>
          {data.notes.map((note) => (
            <li key={note}><FiInfo className={local.noteIcon} />{note}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default DeliveryTable;
